import React from "react";
import { cryptiqueAnalyticsTheme as themeDefault } from "../theme";
import { formatNumber } from "../../../../shared/utils/numberFormatter";
import { makeFormatValue } from "../utils/axisFormat";

/**
 * Funnel metric view that shows Mixpanel-style breakdown tiles only.
 * No overall / first / final step cards are rendered here to match Mixpanel UI.
 */
export default function FunnelMetricView({
  steps = [],
  breakdowns = [],
  measuring = "conversion_rate",
  config,
  theme = themeDefault,
}) {
  const effectiveTheme = theme || themeDefault;
  const hasBreakdowns = Array.isArray(breakdowns) && breakdowns.length > 0;

  // Axis-formatting: respect funnelAxis + left/right Y config so tiles match charts/tables
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const funnelAxis = config?.funnelAxis ?? "left";
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);

  // Helper: pick display count/value from a step object depending on measuring type
  const getStepDisplayValue = (stepInfo = {}) => {
    const { count = 0, value } = stepInfo;
    switch (measuring) {
      case "unique_users":
        return formatNumber(value !== undefined ? value : count);
      case "total_conversions":
      case "total_sessions":
      case "property_sum":
        return formatNumber(value !== undefined ? value : 0);
      case "time_to_convert":
        // For metric tiles we usually care about volume; fall back to count
        return formatNumber(count);
      case "conversion_rate":
      default:
        return formatNumber(count);
    }
  };

  if (!hasBreakdowns) {
    // If there is no breakdown configured, render nothing here;
    // the main funnel visualization and table will still be visible.
    return null;
  }

  return (
    <div
      className="space-y-2"
      style={{ fontFamily: "Raleway, sans-serif" }}
    >
      <div
        className="text-xs font-semibold uppercase"
        style={{ color: effectiveTheme.textMuted }}
      >
        Breakdown
      </div>

      {/* Mixpanel-style grid of tiles; no overall metric cards */}
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {breakdowns.map((segment, index) => {
          const label =
            segment.breakdownDisplay ||
            segment.breakdownValue ||
            segment.breakdownKey ||
            "Other";

          const segmentSteps = segment.steps || {};
          const firstStepData = segmentSteps.step1 || {};
          const lastStepData = segmentSteps[`step${steps.length}`] || {};

          const startVal = getStepDisplayValue(firstStepData);
          const endVal = getStepDisplayValue(lastStepData);

          const conversion =
            typeof segment.overallConversion === "number"
              ? segment.overallConversion
              : firstStepData.count > 0
              ? ((lastStepData.count || 0) / firstStepData.count) * 100
              : 0;

          const colors = [
            "#7c3aed",
            "#3b82f6",
            "#10b981",
            "#f59e0b",
            "#ef4444",
            "#8b5cf6",
            "#06b6d4",
            "#84cc16",
          ];
          const color = colors[index % colors.length];

          return (
            <div
              key={index}
              className="rounded-lg p-3 border shadow-sm cursor-pointer"
              style={{
                borderColor: effectiveTheme.border,
                backgroundColor: effectiveTheme.bgRaised || "#ffffff",
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-2.5 h-2.5 rounded"
                  style={{ backgroundColor: color }}
                />
                <div
                  className="text-xs font-semibold"
                  style={{ color: effectiveTheme.textPrimary }}
                >
                  {label}
                </div>
              </div>

              <div
                className="text-sm font-semibold"
                style={{ color: effectiveTheme.textPrimary }}
              >
                {endVal}{" "}
                <span
                  className="text-xs font-normal"
                  style={{ color: effectiveTheme.textMuted }}
                >
                  total
                </span>
              </div>

              <div
                className="text-xs mt-1"
                style={{ color: effectiveTheme.textMuted }}
              >
                {startVal} → {endVal}
              </div>
              <div
                className="text-xs mt-1"
                style={{ color: effectiveTheme.textMuted }}
              >
                {formatYValue(conversion)} converted
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

